/* ==========================================================================
   Whole dollars read as whole dollars

   "$22.00" becomes "$22"; "$137.27" is left alone. Shopify's own currency
   format is all-or-nothing — the no-decimals format would turn $137.27 into
   $137 — and the price is printed from dozens of places (Dawn's price snippet,
   the drawer, the wholesale builder, five gallery scripts that compute their
   own totals), so this runs once over the rendered page instead.

   It only ever touches text nodes, never a field's value or a script, so
   nothing that is read back — a quantity box, a form, an app's own state —
   can be changed by it. Prices that arrive later, with a cart update or an
   app's drawer, are caught by an observer that batches a burst of mutations
   into one pass.
   ========================================================================== */
(function () {
  'use strict';

  if (!window.gioNoCents) return;

  // Currency first, so a stray "1.00" in body copy is not touched.
  var MONEY = /([$€£¥]\s?\d{1,3}(?:,\d{3})*|[$€£¥]\s?\d+)\.00(?!\d)/g;
  var SKIP = { SCRIPT: 1, STYLE: 1, TEXTAREA: 1, NOSCRIPT: 1, CODE: 1, PRE: 1, INPUT: 1, SELECT: 1 };

  /* The currency apps (seal-money / conversion-bear) do not merely format
     their prices once: they re-scan their own area on a timer and rewrite any
     money-looking TEXT back to $25.00 — including a clean twin placed beside
     theirs, which is what the flicker on load was. Text cannot win that.

     So the clean price is not text at all. The app's span keeps its own text,
     collapsed to nothing, and the value the shopper reads is drawn by a
     ::after whose content comes from a custom property. The app rewrites the
     hidden text as often as it likes; a pseudo-element is not in the DOM it
     scans, so there is nothing to fight over and nothing to repaint.

     Font size is carried in a property because the span's own text is
     collapsed to zero; colour, family and weight inherit as usual, so a
     struck-through or raspberry price keeps its treatment. */
  var APP_SPAN = '.seal-money, .conversion-bear-money, .money';

  function quote(text) {
    return '"' + String(text).replace(/["\\]/g, '\\$&') + '"';
  }

  function unmirror(span) {
    if (!span.classList.contains('gio-cents-donor')) return;
    span.classList.remove('gio-cents-donor');
    span.style.removeProperty('--gio-clean');
    span.style.removeProperty('--gio-clean-fs');
    span.style.removeProperty('--gio-clean-deco');
    span.removeAttribute('data-gio-clean');
  }

  /* Money at all, with cents or without. A span is taken over as soon as the
     page is parsed, while its text is still the clean "$25" the theme wrote,
     rather than waiting for an app to turn it into "$25.00" — that wait was
     the flicker: the shopper saw $25, then $25.00, then $25 again. Armed up
     front the span's own text is already collapsed to nothing, so the app can
     rewrite it as often as it likes and none of it is ever painted. */
  var HAS_MONEY = /[$€£¥]\s?\d/;

  function mirror(span) {
    // A wrapper's children would be collapsed along with its text.
    if (span.children.length) {
      unmirror(span);
      return;
    }
    var raw = String(span.textContent || '');
    if (!HAS_MONEY.test(raw)) {
      unmirror(span);
      return;
    }
    MONEY.lastIndex = 0;

    var clean = raw.replace(MONEY, '$1').trim();
    if (span.getAttribute('data-gio-clean') !== clean) {
      span.setAttribute('data-gio-clean', clean);
      span.style.setProperty('--gio-clean', quote(clean));
    }
    if (!span.classList.contains('gio-cents-donor')) {
      // Measured before the class collapses the span's own type. The strike on
      // a was-price is carried over too: left on the span, the browser drew it
      // across text collapsed to nothing, which put the line above the price
      // instead of through it.
      var before = getComputedStyle(span);
      span.style.setProperty('--gio-clean-fs', before.fontSize);

      /* A was-price is struck by an ANCESTOR (the app wraps it in its own
         .sls-original-price), and that line is drawn across this span's text
         — which is about to collapse to nothing, so it landed above the
         number rather than through it. The strike is moved onto the drawn
         price and switched off where it came from. */
      var deco = 'none';
      var hop = span;
      for (var up = 0; up < 3 && hop; up++) {
        var line = getComputedStyle(hop).textDecorationLine;
        if (line && line.indexOf('line-through') !== -1) {
          deco = 'line-through';
          if (hop !== span) hop.classList.add('gio-cents-struck');
          break;
        }
        hop = hop.parentElement;
      }
      span.style.setProperty('--gio-clean-deco', deco);
      span.classList.add('gio-cents-donor');
    }
  }

  function scrub(root) {
    if (!root || root.nodeType === 8) return;
    if (root.nodeType === 3) {
      if (root.nodeValue.indexOf('.00') !== -1) root.nodeValue = root.nodeValue.replace(MONEY, '$1');
      return;
    }
    if (root.nodeType !== 1) return;
    if (SKIP[root.tagName] || root.isContentEditable) return;

    if (root.querySelectorAll) {
      var spans = root.querySelectorAll(APP_SPAN);
      for (var i = 0; i < spans.length; i++) mirror(spans[i]);
    }
    if (root.matches && root.matches(APP_SPAN)) mirror(root);

    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (node.nodeValue.indexOf('.00') === -1) return NodeFilter.FILTER_REJECT;
        var parent = node.parentNode;
        if (!parent || SKIP[parent.tagName] || parent.isContentEditable) return NodeFilter.FILTER_REJECT;
        // App-owned money spans are drawn by CSS above, never edited here.
        if (parent.closest && parent.closest(APP_SPAN)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });

    var hits = [];
    var node;
    while ((node = walker.nextNode())) hits.push(node);
    hits.forEach(function (text) {
      if (stubborn(text, text.nodeValue)) return;
      text.nodeValue = text.nodeValue.replace(MONEY, '$1');
    });
  }

  /* Loop breaker. A text node this script has already cleaned that comes back
     with the same cents is being re-imposed by another writer; cleaning it
     again just hands the turn back. After three rounds inside a second the
     node is left to that writer and the fight is reported, so it shows up as
     a wrong price in one place instead of a flicker across the page. The
     gallery scripts no longer write cents, so this should never fire. */
  var seen = typeof WeakMap === 'function' ? new WeakMap() : null;
  function stubborn(node, raw) {
    if (!seen) return false;
    var now = Date.now();
    var s = seen.get(node);
    if (!s || s.v !== raw || now - s.t > 1000) {
      seen.set(node, { v: raw, n: 1, t: now, said: false });
      return false;
    }
    s.n++;
    if (s.n <= 3) return false;
    if (!s.said && window.console) {
      s.said = true;
      var p = node.parentElement;
      console.warn('[gio-no-cents] another script keeps rewriting', p ? p.tagName + '.' + p.className : node, JSON.stringify(raw), '— leaving it alone');
    }
    return true;
  }

  function boot() {
    scrub(document.body);

    if (typeof MutationObserver === 'undefined') return;

    /* The observer stays for the life of the page. Its own rewrites do fire
       it once more, but a pass over already-clean text replaces nothing and
       produces no further mutations, so the loop terminates by itself — and
       nothing an app writes late can ever be missed, which is how "$25.00"
       used to survive in the subscription widget on a slow load. */
    var queued = false;
    var observer = new MutationObserver(function () {
      scrub(document.body);
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
